import {
  CreatureData,
  CreatureStateType,
  CreatureType,
  GoblinConfig,
  HostilityType,
  MMONetSchema,
} from "mmo-common";
import { ConfigurableMeshGameObject } from "../utils/configurableMeshGameObject";
import { GoblinMesh } from "./goblinMesh";
import { toThreeQuaternion } from "realms-engine-browser";
import THREE from "realms-three";
import { quatToVector2D, vector2Angle } from "realms-engine";
import { MMONetworkSlave } from "@utils/types";
import { NetworkTypeEvent } from "realms-engine-network";
import { CreatureUIGameObject } from "../ui/creature-ui/creatureUI.gameObject";
import { MMOCreature } from "@game/models/mmoCreature";

const GOBLIN_ANIMATION_MAP = {
  walk: "goblinWalkAnimation",
  walkLeft: "goblinWalkLeftAnimation",
  walkRight: "goblinWalkRightAnimation",
  walkBack: "goblinWalkBackAnimation",
  idle: "goblinIdleAnimation",
  death: "goblinDeathAnimation",
  jump: "goblinJumpAnimation",
  attack: "goblinAttackAnimation",
};

export class GoblinGameObject
  extends ConfigurableMeshGameObject<GoblinConfig, typeof GOBLIN_ANIMATION_MAP>
  implements MMONetworkSlave<CreatureType.Goblin>, MMOCreature
{
  private ui: CreatureUIGameObject;
  private masterState: CreatureData;

  constructor(config: GoblinConfig, data: CreatureData) {
    super(new GoblinMesh(config), GOBLIN_ANIMATION_MAP);
    this.ui = this.addChild(new CreatureUIGameObject(data));
    this.masterState = data;
  }

  isDead(): boolean {
    return this.masterState.state.type === CreatureStateType.Death;
  }

  getHostility(): HostilityType {
    return this.masterState.hostility;
  }

  onNetworkEvent(
    event: NetworkTypeEvent<MMONetSchema, CreatureType.Goblin, "onHurt">
  ): void {
    switch (event.name) {
      case "onHurt": {
        this.onDamage(event.data.damage);
        break;
      }
    }
  }

  onSlaveChanges(changes: Partial<CreatureData>, newState: CreatureData): void {
    this.masterState = newState;
    if (changes.position !== undefined) {
      this.setPosition(newState.position);
    }
    if (changes.quaternion !== undefined) {
      this.setQuaternion(
        toThreeQuaternion(newState.quaternion).multiply(
          new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(0, 1, 0),
            (3 * Math.PI) / 2
          )
        )
      );
    }

    if (changes.health !== undefined) {
      this.ui.setHealth(newState.health);
    }

    if (changes.maxHealth !== undefined) {
      this.ui.setMaxHealth(newState.maxHealth);
    }

    if (changes.state?.type === CreatureStateType.Death) {
      this.getConfigurableMesh().tweenOpacity(0);
      this.ui.setVisibility(false);
    }

    if (changes.state !== undefined) {
      this.playAnimation(this.mapStateToAnimation(newState));
    }

    if (changes.name !== undefined) {
      this.ui.setCreatureName(newState.name);
    }
  }

  mapStateToAnimation(state: CreatureData): {
    name: keyof typeof GOBLIN_ANIMATION_MAP;
    duration: number | null;
  } {
    switch (state.state.type) {
      case CreatureStateType.Idle: {
        return {
          name: "idle",
          duration: null,
        };
      }
      case CreatureStateType.Walk: {
        const faceDirection = quatToVector2D(state.quaternion);
        const angleDifference =
          vector2Angle(faceDirection) - vector2Angle(state.moveDirection);
        return {
          name:
            angleDifference > Math.PI / 4 &&
            angleDifference <= (7 * Math.PI) / 8
              ? "walkLeft"
              : angleDifference < -Math.PI / 4 &&
                angleDifference >= (-7 * Math.PI) / 8
              ? "walkRight"
              : angleDifference > (7 * Math.PI) / 8 ||
                angleDifference < (-7 * Math.PI) / 8
              ? "walkBack"
              : "walk",
          duration: null,
        };
      }
      case CreatureStateType.Death: {
        return {
          name: "death",
          duration: null,
        };
      }
      case CreatureStateType.Jump: {
        return {
          name: "jump",
          duration: null,
        };
      }
      case CreatureStateType.NormalAttack: {
        return {
          name: "attack",
          duration: state.state.duration,
        };
      }
      default: {
        throw new Error(`No animation for state: ${state}`);
      }
    }
  }
}
